import React, { PropsWithChildren, useCallback, useContext, useMemo, useRef } from 'react';

import { CustomBottomSheetModalProvider } from '@/feature/alerts/components/CustomBottomSheetModalProvider';
import { AddLineupModal, AddLineupModalRef, AddLineupProps } from '@/feature/entry-share/screens/AddLineupModal';

type AddLineupContextType = {
    showAddLineupModal: (props: AddLineupProps) => void;
};

const AddLineupContext = React.createContext<AddLineupContextType | undefined>(undefined);

/**
 * Provides access to the Modal used to display the lineup shared via link
 */
export const AddLineupModalProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const addLineupModalRef = useRef<AddLineupModalRef>(null);

    const showAddLineupModal = useCallback((props: AddLineupProps) => {
        addLineupModalRef.current?.show(props);
    }, []);

    const value = useMemo(() => ({ showAddLineupModal }), [showAddLineupModal]);

    return (
        <CustomBottomSheetModalProvider>
            <AddLineupContext.Provider value={value}>
                {children}
                <AddLineupModal ref={addLineupModalRef} />
            </AddLineupContext.Provider>
        </CustomBottomSheetModalProvider>
    );
};

export const useAddLineup = () => {
    const context = useContext(AddLineupContext);
    if (context === undefined) {
        throw new Error('useAddLineup must be used within an AddLineupModalProvider');
    }
    return context;
};
